import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContainer, ProgressCircle, ToastQueue, View } from '@adobe/react-spectrum';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { useReefConfig } from '../../../../hooks/UseReefConfig';
import { ListDocumentRequest } from '../../../../services/soap/document_library/requests/ListDocumentRequest';
import { invokeNewMessageCreating } from '../../../../utils/NavigationUtils';
import DocumentPreviewComponent, { PreviewDocument } from '../../../../components/protected/layout-components/DocumentPreview/DocumentPreviewComponent';
import TaskDocumentUploadDialog from './TaskDocumentUploadDialog';
import { DeleteConfirmationDialog } from '../../finder/components/DeleteConfirmationDialog';
import { UpdateTaskRequest } from '../../../../services/soap/scheduler/requests/UpdateTaskRequest';
import { FPADataTypes } from '../../../../infra/protected/FPA/FPAData';

interface ITaskDocumentsViewProps {
  task: any;
  refreshKey?: number;
  onRefreshTask?: () => Promise<void> | void;
}

const normalizeTaskDocuments = (documentValue: any): PreviewDocument[] => {
  if (!documentValue) return [];
  const documents = Array.isArray(documentValue) ? documentValue : [documentValue];
  return documents
    .map((item: any) => ({
      documentId: String(item?.documentId || item?.id || ''),
      name: String(item?.name || item?.fileName || ''),
      fileName: String(item?.fileName || item?.name || ''),
      mimeContentType: String(item?.mimeContentType || ''),
    }))
    .filter((item) => item.documentId);
};

const mergeDocuments = (...collections: PreviewDocument[][]): PreviewDocument[] => {
  const map = new Map<string, PreviewDocument>();
  collections.flat().forEach((document) => {
    if (!document?.documentId) return;
    map.set(String(document.documentId), document);
  });
  return Array.from(map.values());
};

const TaskDocumentsView: React.FC<ITaskDocumentsViewProps> = ({ 
  task, 
  refreshKey = 0, 
  onRefreshTask }) => {
  const { baseUrl } = useReefConfig();
  const { documentLibraryService, schedulerService, store } = useDependency();
  const [showLoader, setShowLoader] = useState(true);
  const [documents, setDocuments] = useState<PreviewDocument[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);
  const [documentToDelete, setDocumentToDelete] = useState<PreviewDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const taskId = task?.ident || task?.id;
  const fallbackDocuments = useMemo(() => normalizeTaskDocuments(task?.DOCUMENT), [task?.DOCUMENT]);

  useEffect(() => {
    let isActive = true;

    const loadDocuments = async () => {
      if (!taskId) {
        if (isActive) {
          setDocuments(fallbackDocuments);
          setShowLoader(false);
        }
        return;
      }

      setShowLoader(true);
      try {
        const response = await documentLibraryService.listDocument(
          new ListDocumentRequest(store.Server, store.SessionId, {
            limit: 100,
            offset: 0,
            parent: String(taskId),
            orderBy: 'doc_file_name',
          })
        );

        const listedDocuments = Array.isArray(response?.DOCUMENT)
          ? response.DOCUMENT
          : response?.DOCUMENT
            ? [response.DOCUMENT]
            : [];

        const normalized = listedDocuments
          .map((item: any) => ({
            documentId: String(item?.id || item?.documentId || ''),
            name: String(item?.name || item?.fileName || ''),
            fileName: String(item?.fileName || item?.name || ''),
            mimeContentType: String(item?.mimeContentType || ''),
          }))
          .filter((item: PreviewDocument) => item.documentId);

        if (isActive) {
          setDocuments(mergeDocuments(fallbackDocuments, normalized));
        }
      } catch {
        if (isActive) {
          setDocuments(mergeDocuments(fallbackDocuments));
        }
      } finally {
        if (isActive) {
          setShowLoader(false);
        }
      }
    };

    loadDocuments();
    return () => {
      isActive = false;
    };
  }, [documentLibraryService, fallbackDocuments, internalRefreshKey, refreshKey, store.Server, store.SessionId, taskId]);

  const handleSendDocument = async () => {
    try {
      console.log('documents', task);
      const attachments = await Promise.all(
        documents.map(async (document) => {
          const response = await fetch(
            `${baseUrl}/asp-cgi/doc-download.cgi?instance=${store.Server}&session=${store.SessionId}&documentId=${document.documentId}&disposition=inline`
          );
          const blob = await response.blob();
          const base64Files = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(String(reader.result || ''));
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          return {
            fileName: document.fileName || document.name,
            fileSize: blob.size,
            base64Files,
          };
        })
      );

      const activityIdRaw = String(task?.activityId ?? '').trim();
      const projectIdRaw = String(task?.projectId ?? '').trim();
      const folderIdRaw = String(task?.folderId ?? '').trim();

      const activityId = activityIdRaw && activityIdRaw !== '0' ? activityIdRaw : '';
      const projectId = projectIdRaw && projectIdRaw !== '0' ? projectIdRaw : '';
      const folderId = folderIdRaw && folderIdRaw !== '0' ? folderIdRaw : '';

      const messageTarget = activityId
        ? {
            contextId: activityId,
            contextType: FPADataTypes.ACTIVITY,
            isFolderContext: false,
          }
        : projectId
          ? {
              contextId: projectId,
              contextType: FPADataTypes.PROJECT,
              isFolderContext: false,
            }
          : folderId
            ? {
                contextId: folderId,
                contextType: FPADataTypes.FOLDER,
                isFolderContext: true,
              }
            : {
                contextId: '',
                contextType: '',
                isFolderContext: true,
              };

      console.log('messageTarget', messageTarget);
      invokeNewMessageCreating(
        messageTarget.contextId,
        messageTarget.contextType,
        undefined,
        undefined,
        messageTarget.isFolderContext,
        attachments
      );
    } catch {
      ToastQueue.negative('Unable to prepare documents for sending.', { timeout: 2000 });
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete?.documentId) return;

    try {
      setIsDeleting(true);
      const remainingDocuments = normalizeTaskDocuments(task?.DOCUMENT).filter(
        (item) => String(item.documentId) !== String(documentToDelete.documentId)
      );

      const payload = {
        ...task,
        ident: String(task?.ident || task?.id || ''),
        headId: String(task?.headId || task?.ident || task?.id || ''),
        DOCUMENT: remainingDocuments,
      };

      const response: any = await schedulerService.updateTask(
        new UpdateTaskRequest(store.Server, store.SessionId, payload)
      );

      if (response?.result !== 'OK') {
        ToastQueue.negative(response?.EXCEPTION?.message || response?.EXCEPTION?.reason || 'Unable to delete document.', {
          timeout: 2500,
        });
        return;
      }

      await onRefreshTask?.();
      setInternalRefreshKey((prev) => prev + 1);
      ToastQueue.positive('Document deleted successfully.', { timeout: 2000 });
    } catch (error: any) {
      ToastQueue.negative(error?.message || 'Unable to delete document.', { timeout: 2500 });
    } finally {
      setIsDeleting(false);
      setDocumentToDelete(null);
    }
  };

  if (showLoader) {
    return (
      <View UNSAFE_style={{ padding: '24px', display: 'flex', justifyContent: 'center' }}>
        <ProgressCircle aria-label="Loading documents" isIndeterminate />
      </View>
    );
  }

  return (
    <>
      <DocumentPreviewComponent
        documents={documents}
        baseUrl={baseUrl}
        server={store.Server}
        session={store.SessionId}
        onSendDocument={handleSendDocument}
        onUploadDocument={() => setShowUploadDialog(true)}
        onDeleteDocument={(document) => setDocumentToDelete(document)}
      />
      <TaskDocumentUploadDialog
        isOpen={showUploadDialog}
        task={task}
        onClose={() => setShowUploadDialog(false)}
        onUploaded={async () => {
          await onRefreshTask?.();
          setInternalRefreshKey((prev) => prev + 1);
        }}
      />
      <DeleteConfirmationDialog
        isOpen={!!documentToDelete && !isDeleting}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDeleteDocument}
        itemName={documentToDelete?.name || documentToDelete?.fileName || 'document'}
      />
      <DialogContainer onDismiss={() => undefined}>
        {isDeleting ? (
          <Dialog>
            <View padding="size-200">
              <ProgressCircle aria-label="Deleting document" isIndeterminate />
            </View>
          </Dialog>
        ) : null}
      </DialogContainer>
    </>
  );
};

export default TaskDocumentsView;
